<template>
  <v-dialog :value="value" @input="fechar" persistent max-width="600px">
    <v-card class="pa-2">
      <v-card-title>
        <v-icon left color="primary">mdi-clipboard-text</v-icon>
        <span class="text-h6">{{ tituloTela }}</span>
      </v-card-title>
      <v-card-text>
        <v-alert type="info" outlined dense class="mb-4" v-if="nomeAluno">
          <div class="font-weight-medium">Aluno:</div>
          <div>{{ nomeAluno }}</div>
        </v-alert>

        <ValidationObserver ref="observer">
          <v-form @submit.prevent="submitForm">
            <ValidationProvider
              v-if="mostrarSeletorDeAluno"
              name="Nome do aluno"
              rules="required"
              v-slot="{ errors }"
            >
              <v-autocomplete
                v-model="form.matricula_id"
                :items="matriculasParaExibir"
                item-text="label"
                item-value="id"
                label="Nome do aluno *"
                :error-messages="errors"
                outlined
                dense
                class="mb-2"
                :loading="carregandoMatriculas"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-title>Nenhum aluno encontrado</v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </ValidationProvider>

            <ValidationProvider name="Tipo de justificativa" rules="required" v-slot="{ errors }">
              <v-select
                v-model="form.tipo"
                :items="tipos"
                item-text="label"
                item-value="value"
                label="Selecione uma justificativa *"
                :error-messages="errors"
                outlined
                dense
                class="mb-2"
              />
            </ValidationProvider>

            <v-textarea
              v-model="form.observacao"
              :label="form.tipo === 'OUTROS' ? 'Observação *' : 'Observação (opcional)'"
              outlined
              dense
              rows="2"
              class="mb-2"
              counter="1000"
              maxlength="1000"
            />

            <template v-if="exigeAnexo || form.file || anexoExistente">
              <div class="text-body-2 font-weight-medium mb-1">
                <v-icon small left>mdi-paperclip</v-icon>Anexar arquivo
                <span v-if="exigeAnexo">*</span>
              </div>
              <div
                v-if="!form.file && !(anexoExistente && !form.remover_anexo)"
                class="dropbox"
                :class="{ 'dropbox--over': arrastandoArquivo, 'dropbox--error': erroAnexo }"
                @dragover.prevent="arrastandoArquivo = true"
                @dragleave.prevent="arrastandoArquivo = false"
                @drop.prevent="onDrop"
                @click="$refs.inputArquivo.click()"
              >
                <input
                  ref="inputArquivo"
                  type="file"
                  accept=".pdf"
                  class="dropbox-input"
                  @change="onSelecionarArquivo"
                />
                <v-icon size="40" color="primary">mdi-cloud-upload-outline</v-icon>
                <div class="mb-0">
                  Arraste e solte o arquivo PDF aqui ou clique para selecionar.
                </div>
              </div>
              <div v-else class="anexo-preview mb-2">
                <v-icon color="red lighten-1" class="mr-2">mdi-file-pdf-box</v-icon>
                <div class="flex-grow-1">
                  <div>{{ form.file ? form.file.name : "Atestado anexado" }}</div>
                  <div v-if="form.file" class="text-caption text--secondary">
                    {{ formatarTamanho(form.file.size) }}
                  </div>
                </div>
                <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                <v-btn icon @click="removerArquivo">
                  <v-icon color="error">mdi-trash-can</v-icon>
                </v-btn>
              </div>
              <div v-if="erroAnexo" class="text-caption error--text mt-1 mb-2">
                {{ erroAnexo }}
              </div>
              <div class="text-caption text--secondary mb-3">
                Apenas arquivos PDF são permitidos (máx. 4MB).
              </div>
            </template>
            <input
              v-else
              ref="inputArquivo"
              type="file"
              accept=".pdf"
              class="d-none"
              @change="onSelecionarArquivo"
            />
            <v-btn
              v-if="!exigeAnexo && !form.file && !anexoExistente"
              small
              outlined
              class="mb-3"
              @click="$refs.inputArquivo.click()"
            >
              <v-icon left small>mdi-paperclip</v-icon>Anexar arquivo (opcional)
            </v-btn>

            <template v-if="ehGestor && !editingId">
              <v-radio-group
                v-model="form.status_inicial"
                label="Status inicial da justificativa"
                row
                class="mt-2"
              >
                <v-radio label="Aguardando" value="AGUARDANDO"></v-radio>
                <v-radio label="Confirmado" value="CONFIRMADO"></v-radio>
              </v-radio-group>

              <template v-if="form.status_inicial === 'CONFIRMADO'">
                <v-alert type="info" outlined dense class="mb-3">
                  Atenção: ao criar a justificativa com status "Confirmado", informe o período que
                  será justificado.
                </v-alert>
                <div class="periodo-box mb-2">
                  <div class="periodo-box-label">Período justificado *:</div>
                  <v-row dense>
                    <v-col cols="6">
                      <v-menu
                        v-model="menuDataInicial"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        max-width="290px"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="formatarDataBr(form.data_inicial)"
                            label="Data Inicial"
                            append-icon="mdi-calendar"
                            outlined
                            dense
                            readonly
                            hide-details="auto"
                            :error-messages="erroDataInicial"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <v-date-picker
                          v-model="form.data_inicial"
                          :allowed-dates="naoEhFeriado"
                          @input="menuDataInicial = false"
                        />
                      </v-menu>
                    </v-col>
                    <v-col cols="6">
                      <v-menu
                        v-model="menuDataFinal"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        max-width="290px"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="formatarDataBr(form.data_final)"
                            label="Data Final"
                            append-icon="mdi-calendar"
                            outlined
                            dense
                            readonly
                            hide-details="auto"
                            :error-messages="erroDataFinal"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <v-date-picker
                          v-model="form.data_final"
                          :allowed-dates="naoEhFeriado"
                          @input="menuDataFinal = false"
                        />
                      </v-menu>
                    </v-col>
                  </v-row>
                </div>
              </template>
            </template>
          </v-form>
        </ValidationObserver>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="exigeAnexo && anexoExistente && !form.remover_anexo && editingId"
          outlined
          color="grey darken-1"
          @click="imprimirAtestado"
        >
          <v-icon left small>mdi-printer</v-icon>Imprimir
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text color="grey darken-1" @click="fechar(false)">
          <v-icon left small>mdi-close</v-icon>Cancelar
        </v-btn>
        <v-btn color="primary" :loading="salvando" @click="submitForm">
          <v-icon left small>mdi-check</v-icon>Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mapGetters } from "vuex";

const MAX_FILE_SIZE_MB = 4;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export default {
  components: { ValidationObserver, ValidationProvider },
  props: {
    value: { type: Boolean, default: false },
    editingId: { type: [Number, String], default: null },
  },
  data() {
    return {
      form: {
        matricula_id: null,
        tipo: null,
        observacao: "",
        file: null,
        remover_anexo: false,
        status_inicial: "AGUARDANDO",
        data_inicial: null,
        data_final: null,
      },
      alunoSelecionado: null,
      anexoExistente: false,
      tipos: [],
      feriados: [],
      matriculasOptions: [],
      carregandoMatriculas: false,
      menuDataInicial: false,
      menuDataFinal: false,
      erroDataInicial: "",
      erroDataFinal: "",
      erroAnexo: "",
      salvando: false,
      arrastandoArquivo: false,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    ehAluno() {
      return this.user && this.user.roleName === "aluno";
    },
    ehGestor() {
      return !this.ehAluno;
    },
    mostrarSeletorDeAluno() {
      return this.ehGestor && !this.editingId;
    },
    nomeAluno() {
      if (this.ehAluno) return this.user ? this.user.name : "";
      return this.alunoSelecionado ? this.alunoSelecionado.nomecompleto : "";
    },
    tituloTela() {
      return this.editingId ? "Editar Justificativa de Falta" : "Nova Justificativa de Falta";
    },
    exigeAnexo() {
      return this.form.tipo === "ATESTADO_MEDICO";
    },
    matriculasParaExibir() {
      return this.matriculasOptions.map((matricula) => ({
        id: matricula.id,
        label: `${matricula.aluno ? matricula.aluno.nomecompleto : "- - -"} — ${
          matricula.turma ? matricula.turma.descricao : "sem turma"
        }`,
        aluno: matricula.aluno,
      }));
    },
  },
  watch: {
    value(aberto) {
      if (aberto) {
        this.abrir();
      }
    },
    "form.matricula_id": function (id) {
      const matricula = this.matriculasOptions.find((item) => item.id === id);
      this.alunoSelecionado = matricula ? matricula.aluno : null;
    },
  },
  methods: {
    async abrir() {
      this.resetForm();
      await this.carregarTipos();
      await this.carregarFeriados();
      if (this.mostrarSeletorDeAluno) {
        await this.carregarMatriculas();
      }
      if (this.editingId) {
        await this.carregarSolicitacao();
      }
    },
    resetForm() {
      this.form = {
        matricula_id: null,
        tipo: null,
        observacao: "",
        file: null,
        remover_anexo: false,
        status_inicial: "AGUARDANDO",
        data_inicial: null,
        data_final: null,
      };
      this.alunoSelecionado = null;
      this.anexoExistente = false;
      this.erroDataInicial = "";
      this.erroDataFinal = "";
      this.erroAnexo = "";
      if (this.$refs.observer) this.$refs.observer.reset();
    },
    fechar(valor) {
      this.$emit("input", valor === true);
    },
    async carregarMatriculas() {
      this.carregandoMatriculas = true;
      try {
        this.matriculasOptions =
          await this.$services.justificativaFaltaService.matriculasParaSelecao();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoMatriculas = false;
      }
    },
    naoEhFeriado(data) {
      return !this.feriados.includes(data);
    },
    formatarDataBr(data) {
      if (!data) return "";
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    },
    formatarTamanho(bytes) {
      if (!bytes) return "";
      const mb = bytes / (1024 * 1024);
      if (mb >= 1) return `${mb.toFixed(1)}MB`;
      return `${(bytes / 1024).toFixed(0)}KB`;
    },
    async carregarFeriados() {
      try {
        const eventos = await this.$services.calendarioLetivoService.syncAll();
        this.feriados = eventos
          .filter(
            (evento) =>
              evento.evento &&
              (evento.evento.is_feriado ||
                (evento.evento.legenda || "").toLowerCase().includes("feriado"))
          )
          .map((evento) => evento.start);
      } catch (error) {
        this.$handleError(error);
      }
    },
    async carregarTipos() {
      try {
        this.tipos = await this.$services.justificativaFaltaService.tipos();
      } catch (error) {
        this.$handleError(error);
      }
    },
    async carregarSolicitacao() {
      try {
        const solicitacao = await this.$services.justificativaFaltaService.find(this.editingId);
        this.form.tipo = solicitacao.tipo;
        this.form.observacao = solicitacao.observacao;
        this.anexoExistente = !!solicitacao.anexo;
        if (solicitacao.aluno) {
          this.alunoSelecionado = solicitacao.aluno;
        }
      } catch (error) {
        this.$handleError(error);
      }
    },
    validarESelecionarArquivo(file) {
      if (!file) return;
      if (file.type !== "application/pdf") {
        this.$toast.warn("Apenas arquivos PDF são permitidos");
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        this.$toast.warn(`O arquivo não pode ser maior que ${MAX_FILE_SIZE_MB}MB`);
        return;
      }
      this.form.file = file;
      this.form.remover_anexo = false;
      this.erroAnexo = "";
    },
    onSelecionarArquivo(event) {
      this.validarESelecionarArquivo(event.target.files[0]);
      event.target.value = "";
    },
    onDrop(event) {
      this.arrastandoArquivo = false;
      this.validarESelecionarArquivo(event.dataTransfer.files[0]);
    },
    removerArquivo() {
      this.form.file = null;
      this.form.remover_anexo = true;
    },
    validarArquivo() {
      if (!this.exigeAnexo) {
        this.erroAnexo = "";
        return true;
      }
      const temAnexo = this.form.file || (this.anexoExistente && !this.form.remover_anexo);
      this.erroAnexo = temAnexo ? "" : "É necessário anexar o atestado em PDF";
      return !!temAnexo;
    },
    validarPeriodo() {
      if (!this.ehGestor || this.editingId || this.form.status_inicial !== "CONFIRMADO") {
        this.erroDataInicial = "";
        this.erroDataFinal = "";
        return true;
      }
      this.erroDataInicial = this.form.data_inicial ? "" : "O campo Data Inicial é obrigatório";
      this.erroDataFinal = this.form.data_final ? "" : "O campo Data Final é obrigatório";
      return !this.erroDataInicial && !this.erroDataFinal;
    },
    montarFormData() {
      const formData = new FormData();
      if (this.mostrarSeletorDeAluno) {
        formData.append("matricula_id", this.form.matricula_id);
      }
      formData.append("tipo", this.form.tipo);
      formData.append("observacao", this.form.observacao || "");
      if (this.form.file) {
        formData.append("file", this.form.file);
      }
      if (this.form.remover_anexo) {
        formData.append("remover_anexo", "1");
      }
      if (this.ehGestor && !this.editingId) {
        formData.append("status_inicial", this.form.status_inicial);
        if (this.form.status_inicial === "CONFIRMADO") {
          formData.append("data_inicial", this.form.data_inicial);
          formData.append("data_final", this.form.data_final);
        }
      }
      return formData;
    },
    async submitForm() {
      const valido = await this.$refs.observer.validate();
      const periodoValido = this.validarPeriodo();
      if (!valido || !periodoValido) return;
      if (!this.validarArquivo()) return;

      this.salvando = true;
      try {
        const formData = this.montarFormData();
        if (this.editingId) {
          await this.$services.justificativaFaltaService.update(this.editingId, formData);
        } else {
          await this.$services.justificativaFaltaService.store(formData);
        }
        this.$toast.success("Justificativa salva com sucesso!");
        this.$emit("salvo");
        this.fechar(false);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },
    async imprimirAtestado() {
      try {
        await this.$services.justificativaFaltaService.baixarAtestado(this.editingId);
      } catch (error) {
        this.$handleError(error);
      }
    },
  },
};
</script>

<style scoped>
.periodo-box {
  border: 1px solid #ef9a9a;
  border-radius: 4px;
  padding: 12px;
}
.periodo-box-label {
  font-size: 0.9rem;
  margin-bottom: 8px;
}
.dropbox {
  outline: 2px dashed #90caf9;
  outline-offset: -6px;
  background: #e3f2fd;
  color: #616161;
  border-radius: 4px;
  padding: 24px 10px;
  min-height: 130px;
  position: relative;
  cursor: pointer;
  text-align: center;
}
.dropbox--over {
  background: #bbdefb;
}
.dropbox--error {
  outline-color: #ff5252;
}
.dropbox-input {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
}
.anexo-preview {
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
}
</style>
